
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import {
  Box, Container, Grid, Card, CardContent, Typography, Chip, Stack, Divider,
  Button, IconButton, Paper, Modal, Fade, Backdrop, CircularProgress,
  Alert, Dialog, DialogContent, DialogActions,
  useMediaQuery, useTheme, ToggleButton, ToggleButtonGroup,Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";

import { createTheme, ThemeProvider } from "@mui/material/styles";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  CancelOutlined as CancelIcon,
  WarningAmberRounded as WarnIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Visibility as EyeIcon,
} from "@mui/icons-material";
import { apiRequest } from "../../fetchSetup"; // adjust path if needed
import  bg2 from "../Logos/med_background2.png"


// ─── Theme ──────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary:    { main: "#006D77", light: "#4A9FA8", dark: "#003D45" },
    success:    { main: "#16A34A" },
    warning:    { main: "#D97706" },
    error:      { main: "#DC2626" },
    info:       { main: "#007A85" },
    background: { default: "#F9FAFB", paper: "#FFFFFF" },
  },
  typography: {
    fontFamily: "'DM Sans', sans-serif",
    h5: { fontFamily: "'Sora', sans-serif", fontWeight: 900 },
    h6: { fontFamily: "'Sora', sans-serif", fontWeight: 800 },
    button: { fontFamily: "'DM Sans', sans-serif", fontWeight: 700, textTransform: "none" },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: "none", fontWeight: 700 } } },
  },
});

// ─── API ─────────────────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_BASE_URL;
// Download endpoint — user variant (read-only, no upload/delete)
// GET /package-orders/my-bookings/<booking_id>/documents/<filename>
// This should proxy to the same presigned-URL logic, or you can reuse the admin endpoint
// if your backend exposes a user-facing download route. Adjust the base if needed.
const USER_DOWNLOAD_PKG_DOC = process.env.REACT_APP_DOWNLOAD_PKG_BOOKING_DOC
  || `${API}/package-orders/my-bookings`;

const getAuthHeader = () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── Package meta ────────────────────────────────────────────────────────────────
const PKG_META = {
  "pkg-silver":   { label: "Silver",   icon: "🥈", gradient: "linear-gradient(135deg,#006D77,#004E56)" },
  "pkg-gold":     { label: "Gold",     icon: "🥇", gradient: "linear-gradient(135deg,#007A85,#005960)" },
  "pkg-platinum": { label: "Platinum", icon: "💎", gradient: "linear-gradient(135deg,#005C65,#003D45)" },
  "pkg-elite":    { label: "Elite",    icon: "👑", gradient: "linear-gradient(135deg,#008A96,#006370)" },
  "pkg-diabetes": { label: "Diabetes", icon: "🩸", gradient: "linear-gradient(135deg,#004E56,#003040)" },
  "pkg-women":    { label: "Women's",  icon: "🌸", gradient: "linear-gradient(135deg,#009AA6,#007080)" },
};

const STATUS_COLORS = {
  Pending:   { bg: "#FEF3C7", text: "#D97706", label: "Pending"   },
  Confirmed: { bg: "#DBEAFE", text: "#2563EB", label: "Confirmed" },
  Collected: { bg: "#E0E7FF", text: "#6366F1", label: "Collected" },
  Completed: { bg: "#D1FAE5", text: "#059669", label: "Completed" },
  Cancelled: { bg: "#FEE2E2", text: "#DC2626", label: "Cancelled" },
};
const PAYMENT_COLORS = {
  Unpaid:   { bg: "#FEF3C7", text: "#D97706" },
  Paid:     { bg: "#D1FAE5", text: "#059669" },
  Refunded: { bg: "#F1F5F9", text: "#64748B" },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────────
const StatusChip = ({ value }) => {
  const c = STATUS_COLORS[value] || { bg: "#F1F5F9", text: "#64748B", label: value };
  return (
    <Chip label={c.label} size="small"
      sx={{ bgcolor: c.bg, color: c.text, fontWeight: 700, fontSize: 12, borderRadius: "16px", px: 0.5, pointerEvents: "none" }} />
  );
};
const PayChip = ({ value }) => {
  const c = PAYMENT_COLORS[value] || { bg: "#F1F5F9", text: "#64748B" };
  return (
    <Chip label={value} size="small"
      sx={{ bgcolor: c.bg, color: c.text, fontWeight: 700, fontSize: 12, borderRadius: "16px", px: 0.5, pointerEvents: "none" }} />
  );
};

const InfoItem = ({ label, value }) => (
  <Box sx={{ background: "#F8FAFC", borderRadius: 2, p: "9px 12px", border: "1px solid #E5E7EB" }}>
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.3 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827", wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Box>
);

// ─── File icon helper — same as admin ────────────────────────────────────────────
const getFileIcon = (fileName, size = 36) => {
  const ext = (fileName || "").split(".").pop().toLowerCase();
  const sx  = { fontSize: size };
  switch (ext) {
    case "pdf":  return <PdfIcon         sx={{ ...sx, color: "#EF4444" }} />;
    case "png":
    case "jpg":
    case "jpeg": return <ImageIcon       sx={{ ...sx, color: "#3B82F6" }} />;
    case "doc":
    case "docx": return <DescriptionIcon sx={{ ...sx, color: "#2563EB" }} />;
    default:     return <FileIcon        sx={{ ...sx, color: "#6B7280" }} />;
  }
};

const isImageFile = (f = "") => ["png", "jpg", "jpeg", "gif", "webp"].includes(f.split(".").pop().toLowerCase());
const isPdfFile   = (f = "") => f.split(".").pop().toLowerCase() === "pdf";

// ─── User Documents Section ───────────────────────────────────────────────────────
// Read-only: users can preview and download documents uploaded by admin.
// Documents are stored in booking.documents[] — each item has:
//   filename, original_filename, cloudfront_url, s3_key, storage_type, uploaded_at
const UserDocumentsSection = ({ booking }) => {
  const [downloading, setDownloading] = useState(null); // filename being downloaded
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDoc,  setPreviewDoc]  = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null); // { url, isImage, isPdf, filename }
  const [docError,    setDocError]    = useState("");

  const docs = booking.documents || [];

  useEffect(() => {
    return () => {
      if (previewBlob?.url) URL.revokeObjectURL(previewBlob.url);
    };
  }, [previewBlob]);

  // ── Download — mirrors admin handleDownloadDoc exactly ──────────────────────
  // Backend: GET /package-orders/my-bookings/<id>/documents/<filename>
  //   S3   → JSON { success, download_url }  (generate_presigned_download_url)
  //   local → raw file bytes via send_file()
  const handleDownload = async (doc) => {
    const filename = doc.filename;
    setDownloading(filename);
    setDocError("");
    try {
      // If CloudFront URL is already available (S3 uploads always have it),
      // try direct link first — avoids an extra round-trip.
      if (doc.cloudfront_url && doc.storage_type === "s3") {
        const a    = document.createElement("a");
        a.href     = doc.cloudfront_url;
        a.target   = "_blank";
        a.rel      = "noopener noreferrer";
        a.download = doc.original_filename || filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }

      // Fallback: ask backend for a presigned URL or raw bytes
      const response = await apiRequest(
        `${USER_DOWNLOAD_PKG_DOC}/${booking.id}/documents/${filename}`,
        { method: "GET" }
      );
      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const contentType = response.headers.get("content-type") || "";

      // S3 path: JSON { download_url }
      if (contentType.includes("application/json")) {
        const data = await response.json();
        if (data.download_url) {
          const a    = document.createElement("a");
          a.href     = data.download_url;
          a.target   = "_blank";
          a.rel      = "noopener noreferrer";
          a.download = data.filename || filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }
        throw new Error("No download URL in response");
      }

      // Local path: raw bytes
      const blob    = await response.blob();
      if (!blob || blob.size === 0) throw new Error("Empty file");
      const blobUrl = URL.createObjectURL(blob);
      const a       = document.createElement("a");
      a.href        = blobUrl;
      a.download    = doc.original_filename || filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setDocError("Download failed: " + err.message);
    } finally {
      setDownloading(null);
    }
  };

  // ── Preview ──────────────────────────────────────────────────────────────────
  // S3 uploads: open CloudFront URL directly in new tab (no extra request needed).
  // Local uploads: fetch blob from backend and show inline.
  const handlePreview = async (doc) => {
    setDocError("");
    const origName = doc.original_filename || doc.filename;

    // S3 — cloudfront_url is always set; open directly
    if (doc.cloudfront_url && doc.storage_type === "s3") {
      window.open(doc.cloudfront_url, "_blank", "noopener,noreferrer");
      return;
    }

    // Local — fetch blob from backend
    try {
      const response = await apiRequest(
        `${USER_DOWNLOAD_PKG_DOC}/${booking.id}/documents/${doc.filename}`,
        { method: "GET" }
      );
      if (!response.ok) throw new Error(`${response.status}`);

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json();
        if (data.download_url) { window.open(data.download_url, "_blank"); return; }
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) throw new Error("Empty file");
      if (previewBlob?.url) URL.revokeObjectURL(previewBlob.url);

      setPreviewDoc(doc);
      setPreviewBlob({ url: URL.createObjectURL(blob), isImage: isImageFile(origName), isPdf: isPdfFile(origName), filename: origName });
      setPreviewOpen(true);
    } catch (err) {
      setDocError("Preview failed: " + err.message);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewBlob?.url) URL.revokeObjectURL(previewBlob.url);
    setPreviewBlob(null);
    setPreviewDoc(null);
  };

  if (docs.length === 0) return null; // No docs? Show nothing to the user.

  return (
    <>
      <Box sx={{ mt: 2.5 }}>
        {/* Section header */}
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1.25, mb: 1.5,
          pb: 1.25, borderBottom: "1px solid #E5E7EB",
        }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 2,
            background: "linear-gradient(135deg,#006D77,#004E56)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FileIcon sx={{ fontSize: 17, color: "#fff" }} />
          </Box>
          <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 15, color: "#111827" }}>
            Your Documents
          </Typography>
          <Chip label={`${docs.length} File${docs.length > 1 ? "s" : ""}`} size="small"
            sx={{ bgcolor: "#ECFDF5", color: "#059669", fontWeight: 700, fontSize: 11, ml: "auto" }} />
        </Box>

        {docError && (
          <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }} onClose={() => setDocError("")}>
            {docError}
          </Alert>
        )}

        {/* Document cards grid */}
        <Grid container spacing={1.5}>
          {docs.map((doc, idx) => {
            const origName   = doc.original_filename || doc.filename || `Document ${idx + 1}`;
            const uploadedAt = doc.uploaded_at;
            const isLoading  = downloading === doc.filename;

            return (
              <Grid item xs={12} sm={6} key={idx}>
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1.5,
                  p: "10px 14px", borderRadius: 2.5,
                  border: "1.5px solid #E5E7EB",
                  background: "#FAFAFA",
                  transition: "all 0.18s",
                  "&:hover": { borderColor: "#006D77", background: "#F0F9F9", boxShadow: "0 2px 10px rgba(0,109,119,0.1)" },
                }}>
                  {/* File icon */}
                  <Box sx={{ flexShrink: 0 }}>{getFileIcon(origName, 32)}</Box>

                  {/* Name + date */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={origName}>
                      {origName.length > 28 ? `${origName.substring(0, 28)}…` : origName}
                    </Typography>
                    {uploadedAt && (
                      <Typography sx={{ fontSize: 11, color: "#9CA3AF", mt: 0.25 }}>
                        {new Date(uploadedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </Typography>
                    )}
                  </Box>

                  {/* Action buttons */}
                  <Stack direction="row" spacing={0.75} flexShrink={0}>
                    {/* Preview — only if has a cloudfront_url or is a local file we can inline */}
                    <IconButton size="small"
                      title="Preview"
                      onClick={() => handlePreview(doc)}
                      sx={{ bgcolor: "#DBEAFE", "&:hover": { bgcolor: "#BFDBFE" }, width: 32, height: 32 }}>
                      <EyeIcon sx={{ fontSize: 15, color: "#2563EB" }} />
                    </IconButton>

                    {/* Download */}
                    <IconButton size="small"
                      title="Download"
                      onClick={() => handleDownload(doc)}
                      disabled={isLoading}
                      sx={{ bgcolor: "#D1FAE5", "&:hover": { bgcolor: "#A7F3D0" }, width: 32, height: 32 }}>
                      {isLoading
                        ? <CircularProgress size={13} sx={{ color: "#059669" }} />
                        : <DownloadIcon sx={{ fontSize: 15, color: "#059669" }} />
                      }
                    </IconButton>
                  </Stack>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onClose={closePreview} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2.5, py: 1.75, bgcolor: "#006D77",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FileIcon sx={{ color: "#fff", fontSize: 20 }} />
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
              {previewDoc?.original_filename || previewDoc?.filename || "Document Preview"}
            </Typography>
          </Box>
          <IconButton onClick={closePreview} sx={{ color: "#fff" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {previewBlob && (
            previewBlob.isImage ? (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <img src={previewBlob.url} alt={previewBlob.filename}
                  style={{ maxWidth: "100%", maxHeight: 500, borderRadius: 8 }} />
              </Box>
            ) : previewBlob.isPdf ? (
              <iframe src={previewBlob.url} title="PDF Preview"
                style={{ width: "100%", height: 550, border: "none" }} />
            ) : (
              <Box sx={{ p: 5, textAlign: "center" }}>
                <FileIcon sx={{ fontSize: 64, color: "grey.300", mb: 1 }} />
                <Typography color="text.secondary">Preview not available. Please download the file.</Typography>
              </Box>
            )
          )}
        </DialogContent>
        <DialogContent sx={{ pt: 0, pb: 2, display: "flex", justifyContent: "center" }}>
          {previewDoc && (
            <Button variant="contained" startIcon={<DownloadIcon />}
              onClick={() => handleDownload(previewDoc)}
              sx={{ bgcolor: "#006D77", "&:hover": { bgcolor: "#005761" }, borderRadius: 2, px: 3 }}>
              Download
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── Cancel Dialog ────────────────────────────────────────────────────────────────
const CancelDialog = ({ booking, onConfirm, onClose, loading }) => (
  <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
    <DialogContent sx={{ textAlign: "center", pt: 4, pb: 2 }}>
      <Box sx={{ width: 60, height: 60, borderRadius: "50%", bgcolor: "#FEF3C7", border: "2px solid #FDE68A", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
        <WarnIcon sx={{ fontSize: 30, color: "#D97706" }} />
      </Box>
      <Typography sx={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, mb: 1, color: "#111827" }}>
        Cancel Booking?
      </Typography>
      <Typography sx={{ fontSize: 14, color: "#6B7280", mb: 2, lineHeight: 1.7 }}>
        Are you sure you want to cancel this booking?
      </Typography>
      <Box sx={{ bgcolor: "#E5E7EB", borderRadius: 2, py: 1, px: 2, fontFamily: "monospace", fontSize: 14, fontWeight: 800, color: "#006D77" }}>
        {booking.booking_ref}
      </Box>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
      <Button fullWidth variant="outlined" onClick={onClose}
        sx={{ borderColor: "#E5E7EB", color: "#6B7280", py: 1.25, borderRadius: 2, "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F9FAFB" } }}>
        Keep Booking
      </Button>
      <Button fullWidth variant="contained" onClick={() => onConfirm(booking.id)}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <CancelIcon />}
        sx={{ bgcolor: "#DC2626", py: 1.25, borderRadius: 2, fontWeight: 800, "&:hover": { bgcolor: "#B91C1C" } }}>
        {loading ? "Cancelling..." : "Yes, Cancel"}
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── Booking Detail Modal — with documents ────────────────────────────────────────
const BookingDetailModal = ({ booking, onClose, onCancelRequest }) => {
  const muiTheme   = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down("sm"));
  if (!booking) return null;

  const meta      = PKG_META[booking.package_id] || {};
  const savings   = booking.savings || (booking.mrp - booking.offer_price);
  const discPct   = Math.round((savings / booking.mrp) * 100);
  const canCancel = ["Pending", "Confirmed"].includes(booking.status);
  const hasDocs   = (booking.documents || []).length > 0;

    const handleInvoiceDownload=async(order_id)=>{
        try {
              const token = sessionStorage.getItem("token");
  
              const response = await axios.get(
                `${process.env.REACT_APP_API_INVOICE}/package-invoice/download/${order_id}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );
              const download_url=response.data.download_url
              window.open(download_url, "_blank");
            } catch (error) {
              console.error("Invoice download failed:", error);
            }
  }

  return (
    <Modal open onClose={onClose} closeAfterTransition BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300, sx: { backdropFilter: "blur(5px)", backgroundColor: "rgba(0,0,0,0.5)" } }}>
      <Fade in>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%,-50%)",
          width: fullScreen ? "95vw" : 540,
          maxHeight: "92vh", overflowY: "auto",
          bgcolor: "#fff", borderRadius: 3,
          boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
        }}>
          {/* Gradient header */}
          <Box sx={{ background: meta.gradient || "linear-gradient(135deg,#006D77,#003D45)", p: "24px 22px 18px", position: "relative", overflow: "hidden" }}>
            <Box sx={{ position: "absolute", top: -40, right: -40, width: 150, height: 150, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
            <Box sx={{ position: "absolute", bottom: -30, left: -20, width: 110, height: 110, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
            <IconButton onClick={onClose} size="small" sx={{ position: "absolute", top: 12, right: 12, zIndex: 2, bgcolor: "rgba(255,255,255,0.2)", color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography sx={{ fontSize: 32, lineHeight: 1, mb: 0.75 }}>{meta.icon || "📦"}</Typography>
              <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, color: "#fff", fontSize: { xs: 15, sm: 17 }, mb: 0.5 }}>
                {booking.package_name}
              </Typography>
              <Box sx={{ display: "inline-block", bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 12, fontWeight: 800, px: 1.5, py: 0.3, borderRadius: 1.5, fontFamily: "monospace", mb: 1.5 }}>
                {booking.booking_ref}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                <Box>
                  <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.6)", fontWeight: 600, mb: 0.25, textTransform: "uppercase" }}>Offer Price</Typography>
                  <Typography sx={{ fontFamily: "'Sora',sans-serif", fontSize: 26, fontWeight: 900, color: "#80ED99", lineHeight: 1 }}>
                    ₹{booking.offer_price?.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)", textDecoration: "line-through" }}>
                    ₹{booking.mrp?.toLocaleString()}
                  </Typography>
                  <Box sx={{ mt: 0.5, bgcolor: "rgba(16,185,129,0.2)", border: "1px solid rgba(16,185,129,0.4)", color: "#80ED99", fontSize: 11, fontWeight: 800, px: 1.25, py: 0.3, borderRadius: 20, display: "inline-block" }}>
                    Save ₹{savings?.toLocaleString()} ({discPct}% OFF)
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ p: { xs: "14px 14px 22px", sm: "18px 20px 26px" } }}>
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <StatusChip value={booking.status} />
              <PayChip value={booking.payment_status} />
            </Box>

            {/* Appointment */}
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#006D77", textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>Appointment</Typography>
            <Grid container spacing={1} mb={2}>
              <Grid item xs={6}><InfoItem label="Date"      value={booking.preferred_date} /></Grid>
              <Grid item xs={6}><InfoItem label="Time Slot" value={booking.preferred_time} /></Grid>
            </Grid>

            {/* Patient */}
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#006D77", textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>Patient Details</Typography>
            <Grid container spacing={1} mb={2}>
              {[["Name", booking.patient_name], ["Phone", booking.phone], ["Email", booking.email], ["Age", booking.age], ["Gender", booking.gender]].map(([l, v]) => (
                <Grid item xs={6} key={l}><InfoItem label={l} value={v} /></Grid>
              ))}
            </Grid>

            {booking.address && (
              <Box mb={2}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#006D77", textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>Address</Typography>
                <InfoItem label="Address" value={booking.address} />
              </Box>
            )}

            {booking.notes && (
              <Box sx={{ bgcolor: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: 2, p: "10px 12px", mb: 2 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.5 }}>Notes</Typography>
                <Typography sx={{ fontSize: 13, color: "#374151", lineHeight: 1.7 }}>{booking.notes}</Typography>
              </Box>
            )}
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                                              <Button
                                                                variant="contained"
                                                                size="small"
                                                                startIcon={<DownloadIcon />}
                                                                onClick={() => {
                                                                  handleInvoiceDownload(booking.booking_ref)
                                                                }}
                                                                sx={{ 
                                                                  bgcolor: '#006D77', 
                                                                  '&:hover': { bgcolor: '#005560' },
                                                                  textTransform: 'none'
                                                                }}
                                                              >
                                                                Download Invoice
                                                              </Button>
                                                            </Box>

            {/* ── Documents section — read-only for users ────────────────────── */}
            {/* Only rendered when admin has uploaded documents for this booking.  */}
            {/* Each doc: { filename, original_filename, cloudfront_url, s3_key,  */}
            {/*             storage_type, uploaded_at, uploaded_by, file_size }   */}
            {hasDocs && <UserDocumentsSection booking={booking} />}

            {booking.created_at && (
              <Typography sx={{ fontSize: 11, color: "#9CA3AF", textAlign: "center", mt: 2, mb: canCancel ? 1.5 : 0 }}>
                Booked on {booking.created_at}
              </Typography>
            )}

            {canCancel && (
              <Button fullWidth variant="outlined" startIcon={<CancelIcon />}
                onClick={() => onCancelRequest(booking)}
                sx={{ mt: 0.5, py: 1.25, borderRadius: 2, fontWeight: 800, fontSize: 14, borderColor: "#FECACA", color: "#DC2626", "&:hover": { bgcolor: "#FEE2E2", borderColor: "#EF4444" } }}>
                Cancel This Booking
              </Button>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

// ─── CardDocumentsPanel ──────────────────────────────────────────────────────────
// Collapsible inline document panel shown directly on the booking card.
// Users can expand, preview (opens CloudFront URL) and download without opening modal.
const CardDocumentsPanel = ({ booking, docCount }) => {
  const [open,        setOpen]        = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [panelError,  setPanelError]  = useState("");
  const docs = booking.documents || [];

  const handleDownload = async (doc) => {
    const filename = doc.filename;
    setDownloading(filename);
    setPanelError("");
    try {
      // S3: use cloudfront_url directly — fastest, no extra request
      if (doc.cloudfront_url && doc.storage_type === "s3") {
        const a = document.createElement("a");
        a.href     = doc.cloudfront_url;
        a.target   = "_blank";
        a.rel      = "noopener noreferrer";
        a.download = doc.original_filename || filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }
      // Local / fallback: ask backend
      const response = await apiRequest(
        `${USER_DOWNLOAD_PKG_DOC}/${booking.id}/documents/${filename}`,
        { method: "GET" }
      );
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json();
        if (data.download_url) {
          const a = document.createElement("a");
          a.href = data.download_url; a.target = "_blank"; a.rel = "noopener noreferrer";
          a.download = data.filename || filename;
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
          return;
        }
        throw new Error("No download URL in response");
      }
      const blob = await response.blob();
      if (!blob || blob.size === 0) throw new Error("Empty file");
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = doc.original_filename || filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setPanelError("Download failed: " + err.message);
    } finally {
      setDownloading(null);
    }
  };

  const handleView = (doc) => {
    // S3: open CloudFront URL directly in new tab
    if (doc.cloudfront_url && doc.storage_type === "s3") {
      window.open(doc.cloudfront_url, "_blank", "noopener,noreferrer");
      return;
    }
    // Local fallback: try to fetch and open
    apiRequest(`${USER_DOWNLOAD_PKG_DOC}/${booking.id}/documents/${doc.filename}`, { method: "GET" })
      .then(async (r) => {
        if (!r.ok) throw new Error();
        const ct = r.headers.get("content-type") || "";
        if (ct.includes("application/json")) {
          const d = await r.json();
          if (d.download_url) { window.open(d.download_url, "_blank"); return; }
        }
        const blob = await r.blob();
        window.open(URL.createObjectURL(blob), "_blank");
      })
      .catch(() => setPanelError("Could not open file. Try downloading instead."));
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      {/* Collapse toggle header */}
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex", alignItems: "center", gap: 1,
          px: 1.5, py: 0.9, cursor: "pointer",
          bgcolor: open ? "#ECFDF5" : "#F0FDF4",
          border: `1.5px solid ${open ? "#6EE7B7" : "#A7F3D0"}`,
          borderRadius: open ? "10px 10px 0 0" : 2,
          transition: "all 0.18s",
          "&:hover": { bgcolor: "#ECFDF5", borderColor: "#6EE7B7" },
          userSelect: "none",
        }}
      >
        <Box sx={{
          width: 26, height: 26, borderRadius: 1.5, flexShrink: 0,
          bgcolor: "#059669", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <FileIcon sx={{ fontSize: 14, color: "#fff" }} />
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#065F46", flex: 1 }}>
          {docCount} Report{docCount > 1 ? "s" : ""} Ready
        </Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#059669" }}>
          {open ? "Hide ▲" : "View & Download ▼"}
        </Typography>
      </Box>

      {/* Expanded doc rows */}
      {open && (
        <Box sx={{
          border: "1.5px solid #6EE7B7", borderTop: "none",
          borderRadius: "0 0 10px 10px", bgcolor: "#F9FAFB",
          px: 1.5, pt: 1, pb: 1.25,
        }}>
          {panelError && (
            <Alert severity="error" sx={{ mb: 1, borderRadius: 1.5, py: 0.25, fontSize: 12 }}
              onClose={() => setPanelError("")}>
              {panelError}
            </Alert>
          )}
          {docs.map((doc, idx) => {
            const origName  = doc.original_filename || doc.filename || `Document ${idx + 1}`;
            const isLoading = downloading === doc.filename;
            return (
              <Box key={idx} sx={{
                display: "flex", alignItems: "center", gap: 1.25,
                py: 0.9,
                borderBottom: idx < docs.length - 1 ? "1px solid #E5E7EB" : "none",
              }}>
                {/* Icon */}
                <Box sx={{ flexShrink: 0 }}>{getFileIcon(origName, 26)}</Box>

                {/* Name */}
                <Typography sx={{
                  flex: 1, fontSize: 13, fontWeight: 600, color: "#111827",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }} title={origName}>
                  {origName.length > 30 ? `${origName.substring(0, 30)}…` : origName}
                </Typography>

                {/* Uploaded date */}
                {doc.uploaded_at && (
                  <Typography sx={{ fontSize: 10, color: "#9CA3AF", flexShrink: 0, display: { xs: "none", sm: "block" } }}>
                    {new Date(doc.uploaded_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                  </Typography>
                )}

                {/* Action buttons */}
                <Stack direction="row" spacing={0.75} flexShrink={0}>
                  <Button size="small" variant="outlined"
                    startIcon={<EyeIcon sx={{ fontSize: 13 }} />}
                    onClick={(e) => { e.stopPropagation(); handleView(doc); }}
                    sx={{
                      borderColor: "#BFDBFE", color: "#2563EB", borderRadius: 1.5,
                      fontSize: 11, px: 1, py: 0.3, minWidth: 0,
                      "&:hover": { bgcolor: "#DBEAFE", borderColor: "#93C5FD" },
                    }}>
                    View
                  </Button>
                  <Button size="small" variant="contained"
                    startIcon={isLoading
                      ? <CircularProgress size={11} color="inherit" />
                      : <DownloadIcon sx={{ fontSize: 13 }} />
                    }
                    onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                    disabled={isLoading}
                    sx={{
                      bgcolor: "#059669", borderRadius: 1.5,
                      fontSize: 11, px: 1, py: 0.3, minWidth: 0,
                      "&:hover": { bgcolor: "#047857" },
                    }}>
                    {isLoading ? "…" : "Download"}
                  </Button>
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ─── Booking Card — with document badge ──────────────────────────────────────────
const BookingCard = ({ booking, onView, onCancelRequest }) => {
  const meta      = PKG_META[booking.package_id] || {};
  const savings   = booking.savings || (booking.mrp - booking.offer_price);
  const canCancel = ["Pending", "Confirmed"].includes(booking.status);
  // documents[] is set by admin upload; show a badge when present
  const docCount  = (booking.documents || []).length;

  return (
    <Card elevation={2} sx={{ borderRadius: 3, mb: 2, overflow: "hidden",width:550, border: "1px solid #E5E7EB", "&:hover": { boxShadow: "0 3px 12px rgba(0,0,0,0.1)" } }}>
      <CardContent sx={{ p: 2.5 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1, mr: 1 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, flexShrink: 0, background: meta.gradient || "#006D77", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
              {meta.icon || "📦"}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#111827", lineHeight: 1.3 }}>
                {booking.package_name?.replace("Ayuja ", "")}
              </Typography>
              <code style={{ fontSize: 11, color: "#006D77", background: "#E5E7EB", padding: "1px 7px", borderRadius: 5, fontWeight: 700 }}>
                {booking.booking_ref}
              </code>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <StatusChip value={booking.status} />
            <PayChip value={booking.payment_status} />
          </Box>
        </Box>

        {/* Appointment row */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.25 }}>
          {[
            { icon: <CalendarIcon sx={{ fontSize: 13, color: "#006D77" }} />, text: booking.preferred_date },
            { icon: <TimeIcon     sx={{ fontSize: 13, color: "#006D77" }} />, text: booking.preferred_time },
            { icon: <PersonIcon   sx={{ fontSize: 13, color: "#006D77" }} />, text: booking.patient_name  },
          ].filter(x => x.text).map((item, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {item.icon}
              <Typography sx={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{item.text}</Typography>
            </Box>
          ))}
        </Box>

        {/* Inline documents panel — directly accessible without opening the modal */}
        {docCount > 0 && <CardDocumentsPanel booking={booking} docCount={docCount} />}

        {/* Price + actions */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #E5E7EB", pt: 1.5, mt: 0.5, flexWrap: "wrap", gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 12, color: "#6B7280" }}>Offer Price</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
              <Box sx={{ bgcolor: "#22577A", color: "#fff", fontSize: 15, fontWeight: 700, px: 2, py: 0.5, borderRadius: 2, display: "inline-block" }}>
                ₹{booking.offer_price?.toLocaleString()}
              </Box>
              <Typography sx={{ fontSize: 12, color: "#9CA3AF", textDecoration: "line-through" }}>₹{booking.mrp?.toLocaleString()}</Typography>
              {savings > 0 && (
                <Box sx={{ bgcolor: "#E5FDCF", color: "#059669", fontSize: 11, fontWeight: 700, px: 1, py: 0.25, borderRadius: 4, border: "1px solid #D1FAE5" }}>
                  Save ₹{savings?.toLocaleString()}
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {canCancel && (
              <Button variant="outlined" onClick={(e) => { e.stopPropagation(); onCancelRequest(booking); }}
                sx={{ borderColor: "#FECACA", color: "#DC2626", borderRadius: 3, fontWeight: 700, fontSize: 13, px: 2, "&:hover": { bgcolor: "#FEE2E2", borderColor: "#EF4444" } }}>
                Cancel
              </Button>
            )}
            <Button variant="outlined" onClick={() => onView(booking)}
              sx={{ borderColor: "#006D77", bgcolor: "#22577A", color: "#fff", borderRadius: 3, fontWeight: 700, fontSize: 13, px: 2.5, "&:hover": { bgcolor: "#006D77" } }}>
              View Details
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
const BookingTable = ({ bookings, onView, onCancelRequest }) => {
  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 1 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "#006D77" }}>
            <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Package</TableCell>
            <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Booking Ref</TableCell>
            <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Patient</TableCell>
            <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Date & Time</TableCell>
            <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Price</TableCell>
            <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ color: "#fff", fontWeight: 700,textAlign:"center" }}>Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {bookings.map((booking) => {
            const meta = PKG_META[booking.package_id] || {};
            const savings = booking.savings || (booking.mrp - booking.offer_price);
            const canCancel = ["Pending", "Confirmed"].includes(booking.status);
            const docCount = (booking.documents || []).length;

            return (
              <TableRow
                key={booking.booking_ref}
                sx={{
                  "&:hover": { bgcolor: "#F9FAFB" },
                }}
              >
                {/* Package */}
                <TableCell sx={{ fontWeight: 600 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 2,
                        background: meta.gradient || "#006D77",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 16,
                      }}
                    >
                      {meta.icon || "📦"}
                    </Box>
                    {booking.package_name?.replace("Ayuja ", "")}
                  </Box>
                </TableCell>

                {/* Booking Ref */}
                <TableCell>
                  <code
                    style={{
                      fontSize: 12,
                      color: "#006D77",
                      background: "#E5E7EB",
                      padding: "2px 8px",
                      borderRadius: 6,
                      fontWeight: 700,
                    }}
                  >
                    {booking.booking_ref}
                  </code>
                </TableCell>

                {/* Patient */}
                <TableCell sx={{ fontWeight: 500 }}>
                  {booking.patient_name}
                </TableCell>

                {/* Date & Time */}
                <TableCell>
                  <Box>
                    <Typography fontSize={13}>
                      {booking.preferred_date}
                    </Typography>
                    <Typography fontSize={12} color="text.secondary">
                      {booking.preferred_time}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Price */}
                <TableCell>
                  <Box>
                    <Typography fontWeight={700} color="#006D77">
                      ₹{booking.offer_price?.toLocaleString()}
                    </Typography>
                    <Typography
                      fontSize={12}
                      color="text.secondary"
                      sx={{ textDecoration: "line-through" }}
                    >
                      ₹{booking.mrp?.toLocaleString()}
                    </Typography>
                    {savings > 0 && (
                      <Typography fontSize={11} color="#059669">
                        Save ₹{savings?.toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <StatusChip value={booking.status} />
                    <PayChip value={booking.payment_status} />

                    {/* Docs indicator */}
                    {docCount > 0 && (
                      <Chip
                        size="small"
                        label={`${docCount} Docs`}
                        sx={{
                          bgcolor: "#E0F2FE",
                          color: "#0284C7",
                          fontWeight: 600,
                          width: "fit-content",
                        }}
                      />
                    )}
                  </Box>
                </TableCell>

                {/* Actions */}
                <TableCell>
                  <Box display="flex" gap={1} justifyContent="flex-end">
                    {canCancel && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onCancelRequest(booking)}
                        sx={{
                          borderColor: "#FECACA",
                          color: "#DC2626",
                          fontWeight: 600,
                          textTransform: "none",
                          "&:hover": {
                            bgcolor: "#FEE2E2",
                          },
                        }}
                      >
                        Cancel
                      </Button>
                    )}

                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => onView(booking)}
                      sx={{
                        bgcolor: "#006D77",
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": { bgcolor: "#005A63" },
                      }}
                    >
                      View
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};


// ─── Main ─────────────────────────────────────────────────────────────────────────
const MyPackageBookings = () => {
  const navigate = useNavigate();
  const [bookings,     setBookings]     = useState([]);
  const [stats,        setStats]        = useState({});
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [error,        setError]        = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedBook, setSelectedBook] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling,   setCancelling]   = useState(false);
  const [viewMode, setViewMode] = useState("table");


  const filters = [
    { id: "all",       label: "All"       },
    { id: "Pending",   label: "Pending"   },
    { id: "Confirmed", label: "Confirmed" },
    { id: "Collected", label: "Collected" },
    { id: "Completed", label: "Completed" },
    { id: "Cancelled", label: "Cancelled" },
  ];

  const fetchBookings = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.get(`${API}/package-orders/my-bookings`, { headers: getAuthHeader() });
      if (res.data.success) {
        setBookings(res.data.bookings || []);
        setStats(res.data.stats || {});
      } else {
        setError(res.data.error || "Failed to load bookings");
      }
    } catch (e) {
      if (e.response?.status === 401) navigate("/login");
      else setError("Unable to load bookings. Please try again.");
    }
    setLoading(false);
    setRefreshing(false);
  }, [navigate]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const onRefresh = () => { setRefreshing(true); fetchBookings(); };

  const handleCancel = async (bookingId) => {
    setCancelling(true);
    try {
      const res = await axios.patch(
        `${API}/package-orders/my-bookings/${bookingId}/cancel`,
        {},
        { headers: getAuthHeader() }
      );
      if (res.data.success) {
        setCancelTarget(null); setSelectedBook(null);
        await fetchBookings();
      } else {
        alert(res.data.error || "Cancellation failed");
      }
    } catch (e) {
      alert(e.response?.data?.error || "Cancellation failed");
    }
    setCancelling(false);
  };

  const filtered = filterStatus === "all"
    ? bookings
    : bookings.filter((b) => b.status === filterStatus);

  if (loading && !refreshing) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: "#F9FAFB" }}>
          <CircularProgress size={60} sx={{ color: "#006D77" }} />
          <Typography sx={{ mt: 2, fontSize: 16, color: "#6B7280" }}>Loading your bookings...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: "100vh", bgcolor: "#F9FAFB" }}>

        {/* Header */}
        <Paper elevation={2} sx={{ backgroundImage: `
                                    linear-gradient(90deg, rgba(0,109,119,0.9) 0%, rgba(0, 169, 181, 0.61) 100%),
                                    url(${bg2})
                                  `,
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                  backgroundRepeat: "no-repeat",borderRadius:0}}>
          <Container maxWidth="lg">
            <Box sx={{ py: { xs: 1.5, sm: 2, md: 3 }, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, position: "relative" }}>
              <IconButton onClick={() => window.history.back()} sx={{ flexShrink: 0,color:"white", boxShadow: 1, "&:hover": { bgcolor: "#f5f5f5" } }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" sx={{ color: "#ffffff", fontWeight: 600, fontSize: { xs: "1rem", sm: "1.4rem", md: "2rem" }, textAlign: "center", flex: 1, fontFamily: "'Sora', sans-serif" }}>
                My Health Bookings
              </Typography>

            </Box>
          </Container>
        </Paper>

        {/* Filter chips */}
        <Paper elevation={0} sx={{ bgcolor: "transparent", borderBottom: "1px solid #E5E7EB", py: 1.5 }}>
          <Container maxWidth="lg">
            <Box sx={{ display: "flex", gap: 1, overflowX: "auto", "&::-webkit-scrollbar": { display: "none" } }}>
              {filters.map((filter) => (
                <Chip key={filter.id}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>{filter.label}</span>
                      {filter.id !== "all" && (
                        <Box sx={{ bgcolor: "#006D77", color: "#fff", fontSize: 11, fontWeight: 700, px: 0.55, py: 0.55, borderRadius: 1.25, minWidth: 20, textAlign: "center" }}>
                          {bookings.filter((b) => b.status === filter.id).length}
                        </Box>
                      )}
                    </Box>
                  }
                  onClick={() => setFilterStatus(filter.id)}
                  sx={{
                    bgcolor: filterStatus === filter.id ? "#006D77" : "white",
                    color:   filterStatus === filter.id ? "#fff" : "black",
                    fontWeight: 600, fontSize: 14,
                    border: filterStatus === filter.id ? "1px solid white" : "1px solid #006D77",
                    cursor: "pointer",
                    "&:hover": { bgcolor: filterStatus === filter.id ? "#005960" : "#E5E7EB" },
                  }}
                />
              ))}
 {/* Right side controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, val) => val && setViewMode(val)}
        >
          <ToggleButton value="card">
            <ViewModuleIcon />
            Card
          </ToggleButton>

          <ToggleButton value="table">
            <ViewListIcon />
            Table
          </ToggleButton>
        </ToggleButtonGroup>

        <IconButton onClick={onRefresh} disabled={refreshing}>
          <RefreshIcon />
        </IconButton>
      </Box>
               
            </Box>
            
          </Container>
        </Paper>

        {/* Stats */}
        {bookings.length > 0 && (
          <Container maxWidth="lg" sx={{ py: 2 }}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {[
                { label: "Total Bookings", val: bookings.length },
                { label: "Completed",      val: bookings.filter(b => b.status === "Completed").length },
                { label: "Pending",        val: bookings.filter(b => b.status === "Pending").length   },
                { label: "Total Savings",  val: `₹${(stats.total_savings || 0).toLocaleString()}`     },
              ].map((s) => (
                <Paper key={s.label} elevation={1} sx={{ flex: 1, minWidth: 10, bgcolor: "#006D77", p: 2, borderRadius: 3, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 24, fontWeight: 700, color: "#fff", mb: 0.5, fontFamily: "'Sora',sans-serif" }}>{s.val}</Typography>
                  <Typography sx={{ fontSize: 14, color: "#fff" }}>{s.label}</Typography>
                </Paper>
              ))}
            </Box>
            
          </Container>
        )}

        {/* Booking list */}
        <Container maxWidth="lg" sx={{ py: 2, pb: 6 }}>
          {error && (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Alert severity="error" sx={{ borderRadius: 2, mb: 2, maxWidth: 420, mx: "auto" }}>{error}</Alert>
              <Button variant="contained" onClick={fetchBookings} sx={{ bgcolor: "#006D77", borderRadius: 2, fontWeight: 700, "&:hover": { bgcolor: "#005960" } }}>
                Try Again
              </Button>
            </Box>
          )}

          {!error && filtered.length === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 10 }}>
              <Typography sx={{ fontSize: 72, mb: 2 }}>🗓️</Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#111827", mb: 1, fontFamily: "'Sora',sans-serif" }}>No Bookings Found</Typography>
              <Typography sx={{ fontSize: 14, color: "#6B7280", textAlign: "center", mb: 3, px: 4 }}>
                {filterStatus === "all" ? "You haven't booked any health packages yet" : `No ${filterStatus} bookings found`}
              </Typography>
              {filterStatus === "all" && (
                <Button variant="contained" onClick={() => navigate("/")} sx={{ bgcolor: "#006D77", color: "#fff", fontWeight: 700, fontSize: 14, px: 3, py: 1.5, "&:hover": { bgcolor: "#005960" } }}>
                  Browse Health Packages
                </Button>
              )}
            </Box>
          )}


          {/* CONTENT */}
                    {filtered.length === 0 ? (
                      <Typography textAlign="center"></Typography>
                    ) : viewMode === "card" ? (
                      <Grid container spacing={2}>
                         {filtered.map((o) => (
                        <Grid item xs={12} sm={6} md={6} key={o.order_id}>
                          <BookingCard
                            booking={o}
                            onView={setSelectedBook}
                            onCancelRequest={setCancelTarget}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : 
                    <BookingTable
                      bookings={filtered}
                      onView={setSelectedBook}
                      onCancelRequest={setCancelTarget}
                    />
                }
        </Container>

        {selectedBook && (
          <BookingDetailModal booking={selectedBook} onClose={() => setSelectedBook(null)} onCancelRequest={(b) => setCancelTarget(b)} />
        )}
        {cancelTarget && (
          <CancelDialog booking={cancelTarget} onClose={() => setCancelTarget(null)} onConfirm={handleCancel} loading={cancelling} />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default MyPackageBookings;